/**
 * LogFilters - Manages log category filtering, rate limiting, and deduplication.
 */

import { LogMode } from './LogTypes';

export class LogFilters {
  private categoryFlags: Record<string, boolean> = {
    'SYSTEM': false,
    'CONSOLE': false,
    'ERROR': true,
    'network': false,
    'targeting': false,
    'combat': false,
    'stats': false,
    'physics': false,
    'movement': false,
    'INPUT': false,
    'CLICK': false,
    'NAVIGATION': false,
    'mob': false,
    'npc': false,
    'devtools': true,
    '__default__': false,
    'map': true,
    'boot': true,
  };

  private rateLimits: Map<string, { lastTime: number; count: number; suppressed: number }> = new Map();
  private readonly RATE_LIMIT_WINDOW_MS = 5000;
  private readonly RATE_LIMIT_MAX_LOGS = 3;
  
  private lastMessages: Map<string, { message: string; count: number; lastTime: number }> = new Map();
  private readonly DEDUP_WINDOW_MS = 1000;

  public isCategoryEnabled(category: string): boolean {
    const lowerCat = category.toLowerCase();
    if (this.categoryFlags[category] !== undefined) return this.categoryFlags[category];
    if (this.categoryFlags[lowerCat] !== undefined) return this.categoryFlags[lowerCat];
    return this.categoryFlags['__default__'] ?? true;
  }

  public setCategoryFlag(category: string, enabled: boolean): void {
    this.categoryFlags[category] = enabled;
  }

  public getCategoryFlags(): Record<string, boolean> {
    return { ...this.categoryFlags };
  }

  public isRateLimited(category: string, message: string): { limited: boolean, suppressed?: number } {
    const key = `${category}:${message.substring(0, 50)}`;
    const now = Date.now();
    const limit = this.rateLimits.get(key);

    if (!limit) {
      this.rateLimits.set(key, { lastTime: now, count: 1, suppressed: 0 });
      return { limited: false };
    }

    if (now - limit.lastTime > this.RATE_LIMIT_WINDOW_MS) {
      const suppressed = limit.suppressed;
      this.rateLimits.set(key, { lastTime: now, count: 1, suppressed: 0 });
      return { limited: false, suppressed };
    }

    if (limit.count >= this.RATE_LIMIT_MAX_LOGS) {
      limit.suppressed++;
      return { limited: true };
    }

    limit.count++;
    return { limited: false };
  }

  public checkDuplicate(category: string, message: string): number {
    const key = `${category}:${message}`;
    const now = Date.now();
    const existing = this.lastMessages.get(key);

    if (existing && now - existing.lastTime < this.DEDUP_WINDOW_MS) {
      existing.count++;
      existing.lastTime = now;
      return existing.count;
    }

    if (this.lastMessages.size > 100) {
      const cutoff = now - this.DEDUP_WINDOW_MS;
      for (const [k, v] of this.lastMessages) {
        if (v.lastTime < cutoff) this.lastMessages.delete(k);
      }
    }

    this.lastMessages.set(key, { message, count: 1, lastTime: now });
    return 0;
  }

  public applyMode(mode: LogMode): void {
    Object.keys(this.categoryFlags).forEach(k => this.categoryFlags[k] = false);
    this.categoryFlags['ERROR'] = true;

    if (mode === LogMode.STATS) {
      this.categoryFlags['stats'] = true;
      this.categoryFlags['SYSTEM'] = true;
    } else {
      if (mode >= LogMode.MINIMAL) {
        this.categoryFlags['SYSTEM'] = true;
        this.categoryFlags['CONSOLE'] = true;
      }
      if (mode >= LogMode.BALANCED) {
        this.categoryFlags['combat'] = true;
        this.categoryFlags['mob'] = true;
        this.categoryFlags['npc'] = true;
        this.categoryFlags['map'] = true;
        this.categoryFlags['boot'] = true;
        this.categoryFlags['__default__'] = false;
      }
      if (mode >= LogMode.GAMEPLAY) {
        this.categoryFlags['stats'] = true;
        this.categoryFlags['__default__'] = true;
      }
      if (mode >= LogMode.NETWORK) {
        this.categoryFlags['network'] = true;
      }
      if (mode >= LogMode.VERBOSE) {
        this.categoryFlags['physics'] = true;
        this.categoryFlags['movement'] = true;
        this.categoryFlags['INPUT'] = true;
        this.categoryFlags['CLICK'] = true;
        this.categoryFlags['targeting'] = true;
      }
    }
  }
}
